'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { Product, Room, Session } from '@/payload-types';
import { ChatHistoryTurn } from '@/payload/collections/chatHistoryTurn';
import { AITextModel, AIThinkingLevel } from '@/server/ai-helper';
import type { StreamChunk } from '@/server/ai-service';
import { isError } from '@/utilities/serverError';
import { cn } from '@/utilities/ui';
import { SignInButton } from '@clerk/nextjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, Brain, CheckCheck, Globe, Loader2, Menu, PanelRight } from 'lucide-react';
import type { PaginatedDocs } from 'payload';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EnterAnimation, StickyCenterMotion } from '@/components/motion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SettingsMenu from '../_navbar/Settings';
import { LanguageSwitcher } from '../_navbar/LanguageSwitcher';
import { useAuth } from '../_providers/authentication/AuthProvider';
import { useLocalization } from '../_providers/localization/LocalizationProvider';
import { createSession, getSession, searchProducts, updateSessionTitle } from './actions';
import { listTempImages } from './actionsImage';
import { ChatBubble } from './components/ChatBubble';
import { ContactButton } from './components/ContactButton';
import { ModeTabs, type ModeValue } from './components/ModeTabs';
import { QuickPrompts } from './components/QuickPrompts';
import { TempMediaList } from './components/TempMediaList';
import { TempUploadPanel } from './components/TempUploadPanel';
import { ConfigPanel } from './ConfigPanel';
import { HistoryDrawer } from './HistoryDrawer';
import { ProductSearch } from './productSearch';
import { useStore } from './store';
type ChatMessage = ChatHistoryTurn & {
  id: string;
};
const generateGuid = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
const THINKING_LEVEL_OPTIONS: Array<{
  value: AIThinkingLevel;
  label: string;
}> = [{
  value: AIThinkingLevel.deep,
  label: 'Deep'
}, {
  value: AIThinkingLevel.fast,
  label: 'Fast'
}];
const getModelForThinkingLevel = (thinkingLevel: AIThinkingLevel): AITextModel => {
  if (thinkingLevel === AIThinkingLevel.fast) {
    return AITextModel.gemini3_1FlashLitePreview;
  }
  return AITextModel.gemini3ProPreview;
};
export function AIChatClient({
  room
}: {
  room: Room | null;
}) {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    messages: i18n
  } = useLocalization();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [input, setInput] = useState('');
  const {
    guid
  } = useStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [mode, setMode] = useState<ModeValue>('consultant');
  const [centerMode, setCenterMode] = useState(room == null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [thinkingLevel, setThinkingLevel] = useState<AIThinkingLevel>(AIThinkingLevel.deep);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingDismissed, setOnboardingDismissed] = useState(true);
  const showOnboarding = !authLoading && !user && !onboardingDismissed;
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionParam = searchParams.get('session');
  useEffect(() => {
    if (sessionParam) {
      setSessionId(sessionParam);
    } else {
      setSessionId(prev => prev ?? generateGuid());
    }
  }, [sessionParam]);
  const handleSelectSession = (session: Session) => {
    // Already viewing this session — nothing to do
    if (session.guid === sessionId) return;

    // Load the session's chat history
    const chat = session.chat as {
      role: 'user' | 'assistant';
      content: string;
      sources?: string[];
      tempMediaIds?: number[];
    }[] | null;
    if (chat && Array.isArray(chat)) {
      setMessages(chat.map((m, i) => ({
        id: `${session.guid}-${i}`,
        role: m.role,
        content: m.content,
        sources: m.sources || [],
        tempMediaIds: m.tempMediaIds || []
      })));
    } else {
      setMessages([]);
    }
    setSessionId(session.guid);
    setCenterMode(false);
    chatMutation.reset();
    router.replace(`${pathname}?session=${encodeURIComponent(session.guid)}`);
  };
  const handleNewChat = () => {
    setMessages([]);
    setSessionId(generateGuid());
    setCenterMode(true);
    setInput('');
    setProducts([]);
    chatMutation.reset();
    router.replace(pathname);
  };
  const ensureSessionQueryParam = (guid_0: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (params.get('session') === guid_0) return;
    params.set('session', guid_0);
    router.replace(`${pathname}?${params.toString()}`);
  };
  const {
    data: tempImages
  } = useQuery({
    queryKey: ['tempMedia'],
    queryFn: async () => {
      const result = await listTempImages(30, guid);
      if (isError(result)) {
        toast.error(result.message);
        return [];
      }
      return result;
    },
    enabled: !!guid
  });
  const {
    data: initialSession
  } = useQuery({
    queryKey: ['session', sessionParam],
    queryFn: async () => {
      if (!sessionParam) {
        return null;
      }
      const result_0 = await getSession(sessionParam);
      if (!result_0) {
        toast.error(i18n.chat.sessionNotFound);
        return null;
      }
      return result_0;
    },
    enabled: !!sessionParam && !!user
  });
  const chatMutation = useMutation({
    mutationFn: async ({
      prompt,
      history,
      tempMediaIds,
      sessionId: sessionId_0
    }: {
      prompt: string;
      history: {
        role: string;
        content: string;
        tempMediaIds?: number[];
        sources?: string[];
      }[];
      tempMediaIds?: number[];
      sessionId: string;
    }) => {
      setCenterMode(false);
      const assistantMessageId = crypto.randomUUID?.() || String(Date.now());

      // Add an empty assistant message that we'll stream into
      setMessages(prev_0 => [...prev_0, {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        sources: undefined,
        tempMediaIds: undefined
      }]);
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: sessionId_0,
          message: prompt,
          mode: mode,
          roomSlug: room?.slug || '',
          productIds: products.map(product => product.id) || [],
          history: history,
          model: getModelForThinkingLevel(thinkingLevel),
          tempMediaIds: tempMediaIds || []
        })
      });
      if (!response.ok) {
        // Remove the placeholder message on error
        setMessages(prev_1 => prev_1.filter(m_0 => m_0.id !== assistantMessageId));
        throw new Error(i18n.chat.failedToChat);
      }
      const reader = response.body?.getReader();
      if (!reader) {
        setMessages(prev_2 => prev_2.filter(m_1 => m_1.id !== assistantMessageId));
        throw new Error('No response stream');
      }
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const {
          value,
          done
        } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {
          stream: true
        });
        const lines = buffer.split('\n');
        // Keep the last (possibly incomplete) line in the buffer
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const chunk = JSON.parse(line) as StreamChunk;
            if (chunk.type === 'text') {
              setMessages(prev_3 => prev_3.map(m_2 => m_2.id === assistantMessageId ? {
                ...m_2,
                content: m_2.content + chunk.content
              } : m_2));
            } else if (chunk.type === 'sources') {
              setMessages(prev_4 => prev_4.map(m_3 => m_3.id === assistantMessageId ? {
                ...m_3,
                sources: chunk.sources
              } : m_3));
            } else if (chunk.type === 'error') {
              throw new Error(chunk.message);
            }
          } catch (e) {
            if (e instanceof SyntaxError) continue;
            throw e;
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        try {
          const chunk_0 = JSON.parse(buffer) as StreamChunk;
          if (chunk_0.type === 'text') {
            setMessages(prev_5 => prev_5.map(m_4 => m_4.id === assistantMessageId ? {
              ...m_4,
              content: m_4.content + chunk_0.content
            } : m_4));
          } else if (chunk_0.type === 'sources') {
            setMessages(prev_6 => prev_6.map(m_5 => m_5.id === assistantMessageId ? {
              ...m_5,
              sources: chunk_0.sources
            } : m_5));
          }
        } catch {
          // ignore incomplete final chunk
        }
      }
      queryClient.invalidateQueries({
        queryKey: ['tempMedia']
      });
      if (history.length === 0) {
        await updateSessionTitle(sessionId_0, messages.map(m_6 => m_6.content).join('\n')).catch(err => console.error('Failed to update session title:', err));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sessions']
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || i18n.chat.failedToChat);
    }
  });
  const appliedInitialSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionParam || !initialSession) {
      return;
    }
    if (appliedInitialSessionRef.current === initialSession.guid) {
      return;
    }

    // Avoid replacing in-flight streamed content for the active session.
    if (chatMutation.isPending && sessionId === initialSession.guid) {
      return;
    }

    // If this session is already rendered locally, do not overwrite it with
    // a potentially stale server snapshot.
    if (sessionId === initialSession.guid && messages.length > 0) {
      appliedInitialSessionRef.current = initialSession.guid;
      return;
    }
    const chat_0 = initialSession.chat as {
      role: 'user' | 'assistant';
      content: string;
      sources?: string[];
      tempMediaIds?: number[];
    }[] | null;
    if (chat_0 && Array.isArray(chat_0)) {
      setMessages(chat_0.map((m_7, i_0) => ({
        id: `${initialSession.guid}-${i_0}`,
        role: m_7.role,
        content: m_7.content,
        sources: m_7.sources || [],
        tempMediaIds: m_7.tempMediaIds || []
      })));
    } else {
      setMessages([]);
    }
    setSessionId(initialSession.guid);
    setCenterMode(false);
    appliedInitialSessionRef.current = initialSession.guid;
  }, [sessionParam, initialSession, chatMutation.isPending, sessionId, messages.length]);
  const scrollToBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }, 0);
  };
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate line height (approximate, or use computed style)
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight) || 20;
    const maxHeight = lineHeight * 5; // 5 lines max

    // Set height based on content, but cap at maxHeight
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;

    // Enable scrolling if content exceeds max height
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [input]);
  const handleSend = () => {
    const prompt_0 = input.trim();
    if (!prompt_0 && tempImages?.length === 0 && !isUploading || chatMutation.isPending) {
      return;
    }
    handleQuickPrompt(prompt_0);
  };
  const handleQuickPrompt = (text: string) => {
    const activeSessionId = sessionId ?? generateGuid();
    if (!sessionId) {
      setSessionId(activeSessionId);
    }

    // Capture history BEFORE adding the new user message
    const history_0 = messages.map(msg => ({
      role: msg.role,
      content: msg.content,
      tempMediaIds: msg.tempMediaIds || [],
      sources: msg.sources || []
    }));
    const newMessage: ChatMessage = {
      id: crypto.randomUUID?.() || String(Date.now()),
      role: 'user',
      content: text,
      tempMediaIds: tempImages?.map(media => media.id) || []
    };
    setMessages(prev_7 => [...prev_7, newMessage]);
    textareaRef.current?.focus();
    setInput('');
    scrollToBottom();

    // Keep URL in sync immediately after send so reload/share points to this session.
    ensureSessionQueryParam(activeSessionId);

    // If this is the first message, create the session immediately so it appears in history
    if (history_0.length === 0) {
      createSession(activeSessionId, text).then(() => {
        queryClient.invalidateQueries({
          queryKey: ['sessions']
        });
      });
    }
    chatMutation.mutate({
      prompt: text,
      history: history_0,
      tempMediaIds: tempImages?.map(media_0 => media_0.id) || [],
      sessionId: activeSessionId
    });

    // Clear temp images immediately when sending (they are consumed by the message)
    if (tempImages && tempImages.length > 0) {
      queryClient.setQueryData(['tempMedia'], []);
    }
  };
  return <div className="flex flex-1 overflow-hidden">
      {showOnboarding && <div className="bg-background fixed inset-0 z-60 flex items-center justify-center px-5 py-6">
          <LanguageSwitcher className="absolute top-5 right-6" />

          <div className="flex w-full max-w-130 flex-col items-center text-center">
            {onboardingStep === 0 && <div className="animate-in fade-in slide-in-from-bottom-2 flex w-full flex-col items-center">
                <h2 className="george-logo-gradient text-[52px] leading-none tracking-tight" style={{
            fontFamily: 'var(--font-display)'
          }}>
                  Deepthink
                </h2>
                <p className="text-muted-foreground mt-1 text-sm font-medium tracking-wide">
                  {i18n.onboarding.subtitle}
                </p>
                <p className="text-muted-foreground mt-8 max-w-108 text-sm leading-7">
                  {i18n.onboarding.description}
                </p>
                <p className="text-muted-foreground mt-4 max-w-108 text-[13px] leading-6 italic">
                  {i18n.onboarding.mission}
                </p>
              </div>}

            {onboardingStep === 1 && <div className="animate-in fade-in slide-in-from-bottom-2 flex w-full flex-col items-center">
                <h2 className="george-logo-gradient text-[38px] leading-none tracking-tight" style={{
            fontFamily: 'var(--font-display)'
          }}>
                  {i18n.onboarding.principlesTitle}
                </h2>
                <div className="mt-9 flex w-full max-w-110 flex-col gap-6 text-left">
                  {[Brain, CheckCheck, Globe].map((Icon, index) => <div key={index} className="flex items-start gap-3.5">
                      <div className="bg-card border-border mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border">
                        <Icon className="text-muted-foreground h-4.5 w-4.5" />
                      </div>
                      <div>
                        <p className="text-foreground text-sm font-medium">
                          {i18n.onboarding.principles[index]?.title}
                        </p>
                        <p className="text-muted-foreground mt-1 text-[13px] leading-6">
                          {i18n.onboarding.principles[index]?.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>}

            {onboardingStep === 2 && <div className="animate-in fade-in slide-in-from-bottom-2 flex w-full flex-col items-center">
                <h2 className="george-logo-gradient text-[36px] leading-none tracking-tight" style={{
            fontFamily: 'var(--font-display)'
          }}>
                  {i18n.onboarding.clerkLoginTitle}
                </h2>
                <p className="text-muted-foreground mt-5 max-w-96 text-sm leading-7">
                  {i18n.onboarding.clerkLoginHint}
                </p>
                <div className="mt-8">
                  <SignInButton mode="modal" forceRedirectUrl={pathname}>
                    <Button type="button" className="bg-card border-border text-foreground hover:bg-accent rounded-full border px-6 py-2.5">
                      {i18n.onboarding.clerkLoginButton}
                    </Button>
                  </SignInButton>
                </div>
              </div>}

            <div className="mt-10 flex w-full max-w-105 items-center gap-3">
              <button type="button" onClick={() => setOnboardingStep(step => Math.max(step - 1, 0))} className="text-muted-foreground hover:text-foreground w-20 text-sm" style={{
            visibility: onboardingStep === 0 ? 'hidden' : 'visible'
          }}>
                {i18n.onboarding.back}
              </button>
              <div className="flex flex-1 items-center justify-center gap-2">
                {[0, 1, 2].map(step_0 => <span key={step_0} className={cn('bg-border h-1.5 rounded-full transition-all', onboardingStep === step_0 ? 'bg-foreground w-5' : 'w-1.5')} />)}
              </div>
              <button type="button" onClick={() => {
            if (onboardingStep < 2) {
              setOnboardingStep(step_1 => step_1 + 1);
              return;
            }
            setOnboardingDismissed(true);
          }} className={cn("bg-card border-border text-foreground hover:bg-accent w-20 rounded-lg border px-3 py-1.5 text-sm", onboardingStep === 2 ? 'hidden' : '')}>
                {onboardingStep === 2 ? i18n.onboarding.start : i18n.onboarding.next}
              </button>
              <div className={cn(onboardingStep === 2 ? 'w-24 h-8' : 'hidden')}></div>
            </div>
          </div>
        </div>}

      {/* ─── History Drawer ─── */}
      <HistoryDrawer isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onSelectSession={handleSelectSession} onNewChat={handleNewChat} currentSessionId={sessionId} />

      {/* ─── Main column ─── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Toggle button bar */}
        <div className="flex shrink-0 items-center gap-2 px-4 py-1">
          <button type="button" onClick={() => setHistoryOpen(v => !v)} className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-8.5 w-8.5 items-center justify-center rounded-full transition-colors" title={i18n.chat.chatHistory}>
            <Menu className="h-4.5 w-4.5" />
          </button>
          <Link href="/" className="flex items-center gap-1.5 leading-none" onClick={() => {
          handleNewChat();
        }}>
            <span className="george-logo-gradient text-2xl tracking-tight" style={{
            fontFamily: 'var(--font-display)'
          }}>
              Deepthink
            </span>
            <span className="bg-muted border-border text-muted-foreground mt-0.75 rounded px-1.5 py-0.5 text-[9px] font-medium tracking-wide uppercase">
              Beta
            </span>
          </Link>
          <div className="grow"></div>
          {!centerMode && <ContactButton url={window ? window.location.href : pathname} />}
          <Button type="button" onClick={() => setConfigOpen(v_0 => !v_0)} title={i18n.chat.configuration} className="bg-card border-border text-foreground hover:bg-accent flex gap-2 rounded-full border px-3 py-1" size="auto">
            <PanelRight className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{i18n.chat.configuration}</span>
          </Button>
          <SettingsMenu />
        </div>

        {/* Scrollable chat area */}
        <div className={cn('stable flex flex-1 flex-col items-center overflow-y-auto scroll-smooth', centerMode ? 'justify-center' : 'justify-start')} ref={scrollRef}>
          {centerMode ? (/* ─── Welcome screen ─── */
        <EnterAnimation key={sessionId ?? 'welcome'}>
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-5 py-10">
                <h1 className="george-logo-gradient text-[44px] leading-none tracking-tight whitespace-nowrap" style={{
              fontFamily: 'var(--font-display)'
            }}>
                  BigScreen Deepthink
                </h1>

                <ModeTabs value={mode} onChange={setMode} />

                <div className="w-full max-w-140">
                  {tempImages && tempImages.length > 0 && <div className="mb-2 flex flex-wrap gap-3 px-2">
                      <TempMediaList tempMedia={tempImages} />
                    </div>}

                  <div className="george-input-pill border-border bg-muted flex items-end gap-1 rounded-full border p-1 pl-1.5 transition-shadow">
                    <TempUploadPanel uploading={isUploading} onSelectFile={async file => {
                  if (!file) return;
                  try {
                    setIsUploading(true);
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('_payload', JSON.stringify({
                      user: guid
                    }));
                    const response = await fetch('/api/tempMedia', {
                      method: 'POST',
                      body: formData,
                      credentials: 'include'
                    });
                    if (!response.ok) {
                      const data = (await response.json().catch(() => null)) as {
                        error?: string;
                      } | null;
                      toast.error(data?.error || i18n.chat.uploadFailed);
                      return;
                    }
                    toast.success(i18n.chat.imageUploaded);
                    queryClient.invalidateQueries({
                      queryKey: ['tempMedia']
                    });
                  } finally {
                    setIsUploading(false);
                  }
                }} />

                    <textarea ref={textareaRef} value={input} onChange={event => setInput(event.target.value)} placeholder={i18n.chat.typeMessage} onKeyDown={event_0 => {
                  if (event_0.key === 'Enter' && !event_0.shiftKey) {
                    event_0.preventDefault();
                    handleSend();
                  }
                }} className="text-foreground placeholder:text-muted-foreground max-h-30 min-h-5.5 flex-1 resize-none border-none bg-transparent py-1.75 text-[15px] leading-normal outline-none" rows={1} />

                    {/* <Select
                      value={thinkingLevel}
                      onValueChange={(value) =>
                        setThinkingLevel(value as AIThinkingLevel)
                      }
                     >
                      <SelectTrigger className="bg-card border-border text-foreground h-9 w-30 shrink-0 rounded-full px-3 text-[12px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {THINKING_LEVEL_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                     </Select> */}

                    <button onClick={handleSend} disabled={chatMutation.isPending || input.trim().length === 0 && tempImages?.length === 0 && !isUploading} className="bg-card border-border text-foreground hover:bg-accent disabled:bg-muted disabled:text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed">
                      {chatMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <QuickPrompts mode={mode} onSelect={handleQuickPrompt} />
              </div>
            </EnterAnimation>) : (/* ─── Messages ─── */
        <div className="flex w-full max-w-225 flex-col gap-4 self-center px-4 pt-6 pb-80">
              {!room && products && products.length > 0 && <ProductSearch selected={products} onChange={setProducts} searchCollection={params_0 => searchProducts({
            collection: 'product',
            ...params_0
          }) as Promise<PaginatedDocs<Product>>} placeholder="" />}

              {messages.map((message, index_0) => <div key={message.id}>
                  <ChatBubble message={message} isStreaming={chatMutation.isPending && index_0 === messages.length - 1 && message.role === 'assistant'} />
                </div>)}

              {chatMutation.isPending && <div className="george-loading-pill inline-flex w-fit items-center gap-1.25 rounded-full px-5 py-2.5">
                  <span className="george-loading-dot" />
                  <span className="george-loading-dot" />
                  <span className="george-loading-dot" />
                </div>}
            </div>)}
        </div>

        {/* ─── Input area ─── */}
        {!centerMode && <StickyCenterMotion className="w-full shrink-0 self-center px-4 pt-2 pb-4" style={{
        maxWidth: '900px'
      }}>
            {/* Temp images above the input */}
            {tempImages && tempImages.length > 0 && <div className="mb-2 flex flex-wrap gap-3 px-2">
                <TempMediaList tempMedia={tempImages} />
              </div>}

            {/* Input pill */}
            <div className="george-input-pill border-border bg-muted flex items-end gap-1 rounded-full border p-1 pl-1.5 transition-shadow">
              <TempUploadPanel uploading={isUploading} onSelectFile={async file_0 => {
            if (!file_0) return;
            try {
              setIsUploading(true);
              const formData = new FormData();
              formData.append('file', file_0);
              formData.append('_payload', JSON.stringify({
                user: guid
              }));
              const response = await fetch('/api/tempMedia', {
                method: 'POST',
                body: formData,
                credentials: 'include'
              });
              if (!response.ok) {
                const data = (await response.json().catch(() => null)) as {
                  error?: string;
                } | null;
                toast.error(data?.error || i18n.chat.uploadFailed);
                return;
              }
              toast.success(i18n.chat.imageUploaded);
              queryClient.invalidateQueries({
                queryKey: ['tempMedia']
              });
            } finally {
              setIsUploading(false);
            }
          }} />

              <textarea ref={textareaRef} value={input} onChange={event_1 => setInput(event_1.target.value)} placeholder={i18n.chat.typeMessage} onKeyDown={event_2 => {
            if (event_2.key === 'Enter' && !event_2.shiftKey) {
              event_2.preventDefault();
              handleSend();
            }
          }} className="text-foreground placeholder:text-muted-foreground max-h-30 min-h-5.5 flex-1 resize-none border-none bg-transparent py-1.75 text-[15px] leading-normal outline-none" rows={1} />

              {/* <Select
                value={thinkingLevel}
                onValueChange={(value) => setThinkingLevel(value as AIThinkingLevel)}
               >
                <SelectTrigger className="bg-card border-border text-foreground h-9 shrink-0 rounded-full px-3 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {THINKING_LEVEL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
               </Select> */}

              <button onClick={handleSend} disabled={chatMutation.isPending || input.trim().length === 0 && tempImages?.length === 0 && !isUploading} className="bg-card border-border text-foreground hover:bg-accent disabled:bg-muted disabled:text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed">
                {chatMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </button>
            </div>

            <p className="text-muted-foreground mt-2 text-center text-[13px]">
              {i18n.chat.answersDisclaimer}
            </p>
          </StickyCenterMotion>}
      </div>

      {/* ─── Config Panel ─── */}
      <ConfigPanel chatIsPending={chatMutation.isPending} key={sessionId ?? 'new'} isOpen={configOpen} onClose={() => setConfigOpen(false)} messages={messages.map(m_8 => ({
      role: m_8.role,
      content: m_8.content
    }))} />
    </div>;
}